import { useMutation } from "@tanstack/react-query"
import type {
  UseMutationOptions,
  UseMutationResult,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  DeleteOperationChainItemMutationRequest,
  DeleteOperationChainItemMutationResponse,
} from "../types/DeleteOperationChainItem"

type DeleteOperationChainItemClient = typeof client<
  DeleteOperationChainItemMutationResponse,
  Error,
  DeleteOperationChainItemMutationRequest
>
type DeleteOperationChainItem = {
  data: DeleteOperationChainItemMutationResponse
  error: Error
  request: DeleteOperationChainItemMutationRequest
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<DeleteOperationChainItemClient>>
  client: {
    parameters: Partial<Parameters<DeleteOperationChainItemClient>[0]>
    return: Awaited<ReturnType<DeleteOperationChainItemClient>>
  }
}
/**
 * @summary Delete an operation chain item
 * @link /v1/operation-chain/delete-operation-chain-item
 */
export function useDeleteOperationChainItem(
  options: {
    mutation?: UseMutationOptions<
      DeleteOperationChainItem["response"],
      DeleteOperationChainItem["error"],
      DeleteOperationChainItem["request"]
    >
    client?: DeleteOperationChainItem["client"]["parameters"]
  } = {}
): UseMutationResult<
  DeleteOperationChainItem["response"],
  DeleteOperationChainItem["error"],
  DeleteOperationChainItem["request"]
> {
  const { mutation: mutationOptions, client: clientOptions = {} } =
    options ?? {}
  return useMutation<
    DeleteOperationChainItem["response"],
    DeleteOperationChainItem["error"],
    DeleteOperationChainItem["request"]
  >({
    mutationFn: async (data) => {
      const res = await client<
        DeleteOperationChainItem["data"],
        DeleteOperationChainItem["error"],
        DeleteOperationChainItem["request"]
      >({
        method: "post",
        url: `/v1/operation-chain/delete-operation-chain-item`,
        data,
        ...clientOptions,
      })
      return res
    },
    ...mutationOptions,
  })
}
